import { ApolloLink, Observable } from '@apollo/client';
import type { FetchResult, Operation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import {
    clearSessionTokens,
    clearSpyeeTokens,
    clearTokens,
    getSessionTokens,
    isSpyeeMode,
    setSessionTokens,
} from './storage';
import {
    AUTHENTICATION_REQUIRED_CODE,
    AUTHENTICATION_REQUIRED_MESSAGE,
    EXPIRED_TOKEN_CODE,
    NETWORK_FAILURE_MESSAGE,
    SESSION_EXPIRED_MESSAGE,
} from './auth-errors';

type PendingRequest = { retry: () => void; abort: (err: Error) => void };

// Comme totmobile. Un refresh abandonné a pu être traité par le serveur : il retrouve le token au prochain
// essai (fenêtre de grâce, puis récupération d'une rotation perdue), on ne perd donc pas la session.
const REFRESH_TIMEOUT_MS = 10000;

let isRefreshing = false;
let pendingRequests: PendingRequest[] = [];

function resolvePendingRequests(): void {
    pendingRequests.forEach(({ retry }) => retry());
    pendingRequests = [];
}

// Reject queued requests so their observer errors out instead of hanging forever.
// Without this, consumers (e.g. useQuery hooks) would stay in loading state when
// the refresh fails between an in-flight retry and the redirect.
function rejectPendingRequests(reason: Error): void {
    pendingRequests.forEach(({ abort }) => abort(reason));
    pendingRequests = [];
}

/**
 * Pourquoi le refresh n'a pas abouti — la distinction porte tout ce fichier.
 *
 * `session_expired` = le serveur a tranché (401), ou il n'y a plus rien à présenter : la session
 * est morte, on déconnecte. `network_error` = pas de réponse exploitable (API qui redémarre, 5xx,
 * coupure réseau) : on ne sait RIEN de l'état de la session, donc on garde les tokens. Les
 * confondre, c'est jeter un refresh token encore valide au premier hoquet. Même découpage que
 * totops et totmobile.
 */
type RefreshResult =
    | { type: 'success'; accessToken: string; refreshToken: string }
    | { type: 'session_expired'; reason: 'no_token' | 'server_401' | 'partial_response' }
    | { type: 'network_error'; status?: number };

// `still_unauthenticated` : refresh réussi, mais l'opération rejouée avec le token neuf est encore refusée.
type LogoutReason = Extract<RefreshResult, { type: 'session_expired' }>['reason'] | 'still_unauthenticated';

// En mode espion, la session renouvelée est celle du compte espionné, avec son propre en-tête.
function sessionHeaderKey(): 'spiedusertoken' | 'authorization' {
    return isSpyeeMode() ? 'spiedusertoken' : 'authorization';
}

// Serialize refresh across tabs/windows of this origin via Web Locks API.
// Without this, two tabs waking from sleep both POST /auth/refresh with the same
// refresh token — one rotates successfully, the other gets 401 and logs the user out.
async function fetchNewTokens(apiUrl: string): Promise<RefreshResult> {
    const initialRefreshToken = getSessionTokens().refresh;
    if (!initialRefreshToken) return { type: 'session_expired', reason: 'no_token' };

    // Le stockage s'écrit ici, sous le verrou : l'onglet suivant relit le token neuf (ou un stockage vidé)
    // au lieu de représenter celui que le serveur vient de consommer.
    const runRefresh = async (): Promise<RefreshResult> => {
        // Re-read after acquiring the lock: another tab may have rotated while we waited.
        const currentRefreshToken = getSessionTokens().refresh;
        if (!currentRefreshToken) return { type: 'session_expired', reason: 'no_token' };
        if (currentRefreshToken !== initialRefreshToken) {
            const accessToken = getSessionTokens().access;
            if (!accessToken) return { type: 'session_expired', reason: 'no_token' };
            return { type: 'success', accessToken, refreshToken: currentRefreshToken };
        }

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);
        let response: Response;
        try {
            response = await fetch(`${apiUrl}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', webapporigin: 'true' },
                body: JSON.stringify({ refreshToken: currentRefreshToken }),
                signal: controller.signal,
            });
        } finally {
            clearTimeout(timeout);
        }

        if (!response.ok) {
            // Safety net: if storage rotated between request send and response,
            // another tab succeeded — use its tokens instead of forcing logout.
            const stored = getSessionTokens();
            if (stored.refresh && stored.refresh !== currentRefreshToken && stored.access) {
                return { type: 'success', accessToken: stored.access, refreshToken: stored.refresh };
            }

            // Le serveur a vu le token et l'a refusé : rotation déjà consommée, token révoqué ou
            // expiré. C'est le SEUL cas où la session est réellement morte.
            if (response.status === 401) {
                clearSessionTokens();
                return { type: 'session_expired', reason: 'server_401' };
            }

            return { type: 'network_error', status: response.status };
        }

        const data = await response.json();
        // Exiger les DEUX tokens : sans ça, `setTokens` écrivait `undefined` en storage et perdait
        // le refresh token que le serveur vient de consommer. Le serveur a répondu 200, donc la
        // rotation a eu lieu : c'est une session morte, pas un hoquet (même règle que
        // freefood-app). Un corps illisible, lui, fait rejeter `response.json()` et reste
        // transitoire : c'est la signature d'un intermédiaire (proxy, portail captif), pas de l'API.
        if (!data?.accessToken || !data?.refreshToken) {
            clearSessionTokens();
            return { type: 'session_expired', reason: 'partial_response' };
        }
        setSessionTokens(data.accessToken, data.refreshToken);
        return { type: 'success', accessToken: data.accessToken, refreshToken: data.refreshToken };
    };

    if (typeof navigator !== 'undefined' && navigator.locks?.request) {
        return navigator.locks.request('totem-token-refresh', runRefresh);
    }
    return runRefresh();
}

let refreshInFlight: Promise<RefreshResult> | null = null;

/**
 * Un seul `/auth/refresh` à la fois dans l'onglet, qu'il vienne d'une requête refusée ou de la socket
 * (`socket-session.ts`). Entre onglets, c'est le Web Lock de `fetchNewTokens` qui sérialise.
 */
export function refreshSession(apiUrl: string): Promise<RefreshResult> {
    refreshInFlight ??= fetchNewTokens(apiUrl).finally(() => {
        refreshInFlight = null;
    });
    return refreshInFlight;
}

function hasExpiredTokenError(result: FetchResult): boolean {
    return !!result.errors?.some((error) => (error.extensions?.code as string) === EXPIRED_TOKEN_CODE);
}

function isUnauthenticated(result: FetchResult): boolean {
    return !!result.errors?.some(
        (error) =>
            (error.extensions?.code as string) === AUTHENTICATION_REQUIRED_CODE ||
            error.message === AUTHENTICATION_REQUIRED_MESSAGE,
    );
}

// AHEEX : access token expiré. AHERA : le serveur ne reconnaît pas l'access token, mais le refresh token est
// un credential distinct, qu'on essaie avant de déconnecter. Une panne côté serveur ne produit plus d'AHERA :
// c'est un 503, donc une erreur réseau, qui ne passe pas par ici.
function needsTokenRefresh(result: FetchResult): boolean {
    return hasExpiredTokenError(result) || isUnauthenticated(result);
}

/**
 * La requête est partie avec un access token déjà remplacé : un refresh a abouti pendant qu'elle était en vol.
 * La rejouer avec le token stocké suffit, une nouvelle rotation serait inutile.
 */
function replacedTokenOf(operation: Operation): string | null {
    const sentToken: string | undefined = operation.getContext().headers?.[sessionHeaderKey()];
    const storedToken = getSessionTokens().access;
    return storedToken && sentToken && sentToken !== storedToken ? storedToken : null;
}

// Individual expired-token errors are not reported (they are expected, and recovered right here),
// so this forced logout is the auth event worth an alert: it is the one the user actually feels.
// Mirrors the totmobile client, which reports only this transition.
function redirectToLogin(reason: LogoutReason): void {
    Sentry.captureMessage('Token refresh link: session expired, triggering logout', {
        level: 'warning',
        tags: { reason },
    });
    clearTokens();
    window.location.href = '/login';
}

// En mode espion, la session morte est celle du compte espionné (fermé entre-temps, refresh token
// expiré) : l'opérateur, lui, a toujours la sienne. On sort du mode espion au lieu de le déconnecter.
function exitSpyeeMode(reason: LogoutReason): void {
    clearSpyeeTokens();
    window.location.href = `/spying?reason=${reason}`;
}

function endSession(reason: LogoutReason, isSpyeeSession: boolean): void {
    if (isSpyeeSession) {
        exitSpyeeMode(reason);
        return;
    }
    redirectToLogin(reason);
}

/** Session morte constatée par la socket (refresh refusé avant une reconnexion) : même sortie que pour une requête. */
export function expireSession(reason: LogoutReason): void {
    rejectPendingRequests(new Error(SESSION_EXPIRED_MESSAGE));
    endSession(reason, isSpyeeMode());
}

/**
 * Échec sans verdict du serveur : on garde les tokens et on fait remonter une erreur réseau.
 *
 * Les requêtes en attente sont rejetées plutôt que rejouées : elles repartiraient avec le même
 * access token expiré, redéclencheraient un refresh, et boucleraient tant que l'API est absente.
 * Le rejet les sort de `loading`, et la prochaine action retentera, refresh token intact.
 * Le message n'est volontairement PAS `SESSION_EXPIRED_MESSAGE` : `isAuthenticationError` doit
 * laisser passer celui-ci jusqu'au boundary, qui est le seul à pouvoir le dire à l'utilisateur.
 */
function handleTransientFailure({
    observer,
    status,
    cause,
}: {
    // zen-observable n'exporte pas `SubscriptionObserver` ; on ne se sert que de `error`.
    observer: { error: (err: Error) => void };
    status?: number;
    cause?: unknown;
}): void {
    Sentry.captureMessage('Token refresh link: transient failure, keeping tokens', {
        level: 'warning',
        tags: { status: status ? String(status) : 'no_response' },
        extra: { cause: cause instanceof Error ? cause.message : cause },
    });

    const error = new Error(NETWORK_FAILURE_MESSAGE);
    rejectPendingRequests(error);
    observer.error(error);
}

export function createTokenRefreshLink(apiUrl: string, onTokenRefreshed?: () => void): ApolloLink {
    return new ApolloLink((operation, forward) => {
        return new Observable<FetchResult>((observer) => {
            const applyToken = (token: string): void => {
                const headerKey = sessionHeaderKey();
                operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
                    headers: { ...headers, [headerKey]: token },
                }));
            };

            // Rejeu avec le token neuf. Encore en AHERA : le serveur ne reconnaît vraiment pas cette session.
            const replayAfterRefresh = (isSpyeeSession: boolean): void => {
                forward(operation).subscribe({
                    next: (result) => {
                        if (isUnauthenticated(result)) {
                            endSession('still_unauthenticated', isSpyeeSession);
                        }
                        observer.next(result);
                    },
                    error: (error) => observer.error(error),
                    complete: () => observer.complete(),
                });
            };

            // Chaque envoi a son propre drapeau : le `complete` d'un envoi dont on a repris la main ne doit pas
            // terminer l'opération, celui de l'envoi qui le remplace le fera.
            function handle(): void {
                let isHandledHere = false;

                forward(operation).subscribe({
                    next: (result) => {
                        if (!needsTokenRefresh(result)) {
                            observer.next(result);
                            return;
                        }

                        isHandledHere = true;

                        const storedToken = replacedTokenOf(operation);
                        if (storedToken) {
                            applyToken(storedToken);
                            handle();
                            return;
                        }

                        if (isRefreshing) {
                            pendingRequests.push({
                                retry: () => {
                                    applyToken(getSessionTokens().access || '');
                                    replayAfterRefresh(isSpyeeMode());
                                },
                                abort: (err) => observer.error(err),
                            });
                            return;
                        }

                        isRefreshing = true;
                        // Lu au même instant que `refreshSession` : c'est la session qu'il tente de renouveler.
                        const isRefreshingSpyeeSession = isSpyeeMode();

                        refreshSession(apiUrl)
                            .then((tokens) => {
                                if (tokens.type === 'session_expired') {
                                    rejectPendingRequests(new Error(SESSION_EXPIRED_MESSAGE));
                                    endSession(tokens.reason, isRefreshingSpyeeSession);
                                    return;
                                }

                                if (tokens.type === 'network_error') {
                                    handleTransientFailure({ observer, status: tokens.status });
                                    return;
                                }

                                // La socket se rouvre avec le token neuf.
                                onTokenRefreshed?.();
                                applyToken(tokens.accessToken);
                                resolvePendingRequests();
                                replayAfterRefresh(isRefreshingSpyeeSession);
                            })
                            .catch((err) => {
                                // `fetch` qui rejette (API injoignable, DNS, offline, timeout) ou corps
                                // illisible (intermédiaire qui répond à la place de l'API) : aucune
                                // réponse de l'API, donc aucune raison de considérer la session morte.
                                handleTransientFailure({ observer, cause: err });
                            })
                            .finally(() => {
                                isRefreshing = false;
                            });
                    },
                    error: (error) => observer.error(error),
                    complete: () => {
                        if (!isHandledHere) {
                            observer.complete();
                        }
                    },
                });
            }

            handle();
        });
    });
}
